import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { withTranslation } from '@application/i18n/with_translation';
import { parseDateOfBirth } from '@application/parsers';
import { InputText } from '@cellula/react';
import { type SexAssignedAtBirth } from '@feature/information-gathering/information-gathering.model';
import dayjs from 'dayjs';
import { InformationGatheringForm } from './information-gathering-form';
import { SexAssignedAtBirthField } from './sex-assigned-at-birth-field';
import { useInformationGatheringJourney } from './use-information-gathering-journey';

interface PatientDetailsFormData {
	firstName: string;
	lastName: string;
	dateOfBirth: string;
	sexAssignedAtBirth: SexAssignedAtBirth | '';
}

export function PatientDetailsScreen() {
	const { draft, patientIdentity, shouldAskSexAssignedAtBirth, saveDraft, savePatientIdentity, goNext, goBack } =
		useInformationGatheringJourney();
	const shouldAskDateOfBirth = patientIdentity.dateOfBirth === '';

	function submit(data: PatientDetailsFormData) {
		if (shouldAskDateOfBirth) {
			const dateOfBirth = parseDateOfBirth(data.dateOfBirth);
			if (dateOfBirth === null) return;
			savePatientIdentity({ ...patientIdentity, dateOfBirth: dayjs(dateOfBirth).format('YYYY-MM-DD') });
		}

		saveDraft({
			...draft,
			patient: {
				...draft.patient,
				firstName: data.firstName.trim(),
				lastName: data.lastName.trim(),
				sexAssignedAtBirth: data.sexAssignedAtBirth ?? draft.patient.sexAssignedAtBirth,
			},
		});
		goNext();
	}

	return (
		<InformationGatheringForm
			title='View.CreateNewMessage.InformationGathering.PatientDetails.Title'
			description='View.CreateNewMessage.InformationGathering.PatientDetails.Description'
			defaultValues={{
				firstName: draft.patient.firstName,
				lastName: draft.patient.lastName,
				dateOfBirth: patientIdentity.dateOfBirth,
				sexAssignedAtBirth: draft.patient.sexAssignedAtBirth,
			}}
			onSubmit={submit}
			goBack={goBack}
		>
			<InputText
				label={withTranslation('View.CreateNewMessage.InformationGathering.PatientDetails.FirstName')}
				name='firstName'
				required
				options={{ required: I18nAndL10n.translate('Form.ValidationError.Required.FirstName') }}
			/>
			<InputText
				label={withTranslation('View.CreateNewMessage.InformationGathering.PatientDetails.LastName')}
				name='lastName'
				required
				options={{ required: I18nAndL10n.translate('Form.ValidationError.Required.Surname') }}
			/>
			{shouldAskDateOfBirth ? (
				<InputText
					label={withTranslation('View.RedRobin.ScreenWhoIsRespondant.NhsApp.Input.Label.DateOfBirth')}
					description={withTranslation('View.RedRobin.ScreenWhoIsRespondant.NhsApp.Input.Description.DateOfBirthFormat')}
					placeholder='DD/MM/YYYY'
					name='dateOfBirth'
					required
					options={{
						required: I18nAndL10n.translate('Form.ValidationError.Required.DateOfBirth'),
						validate: {
							dateOfBirth: (value: string) =>
								parseDateOfBirth(value) !== null || I18nAndL10n.translate('Form.ValidationError.Required.DateOfBirth'),
						},
					}}
				/>
			) : null}
			{shouldAskSexAssignedAtBirth ? <SexAssignedAtBirthField /> : null}
		</InformationGatheringForm>
	);
}
